import { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import qs from 'qs'

import setUrlParams from 'lib/set-url-params'
import FetchingPanel from 'components/FetchingPanel'
import Pagination from 'components/Pagination'
import ProductsFilter from './ProductsFilter'
import ProductsTable from './ProductsTable'

import GET_PRODUCTS_LIST from './graphql/GetProductsList.graphql'
import {
  ProductFilterArguments,
  ProductSortingArguments,
  ProductSortingEnum,
  SortDirectionEnum,
  TagMatchingModeEnum
} from '../../../../../../__generated__/globalTypes'
import { GetProductsList, GetProductsListVariables } from './graphql/__generated__/GetProductsList'

import styles from './ProductsList.module.css'

export type ProductsListProps = {
  className?: string
  catalogId: string
  page?: number
  perPage?: number
  sort?: ProductSortingArguments
  filter?: ProductFilterArguments
  updatedProductId?: string
  updatePrintableCount?: (count: number) => void
  refreshFirstImage?: boolean
}

const ProductsList = ({
  className,
  catalogId,
  page = 1,
  perPage = 50,
  sort = { sortBy: ProductSortingEnum.POSITION, sortDirection: SortDirectionEnum.ASC },
  filter,
  updatedProductId,
  updatePrintableCount,
  refreshFirstImage
}: ProductsListProps) => {
  const url = new URL(document.location.href, document.location.origin)
  const params: Record<string, string | string[] | undefined | null | boolean | number> = {
    ...filter,
    page
  }

  setUrlParams(params)

  const returnTo =
    Object.values(params).length > 0 ? `?returnTo=${url.pathname}?${qs.stringify(params)}`.replace(/\?$/, '') : ''

  const { loading, data } = useQuery<GetProductsList, GetProductsListVariables>(GET_PRODUCTS_LIST, {
    variables: {
      catalogId,
      page,
      perPage,
      sort,
      filter: { ...filter, customTagMode: TagMatchingModeEnum.ANY }
    }
  })

  const products = data?.currentSeller?.catalog?.draftProducts
  const totalProducts = data?.currentSeller?.catalog?.allDraftProducts?.nodesCount ?? 0
  const outOfStockThreshold = data?.currentSeller.catalog?.outOfStockThreshold
  const currencySymbol = data?.currentSeller.catalog?.currencySymbol ?? '$'

  useEffect(() => {
    updatePrintableCount && updatePrintableCount(products?.nodesCount ?? 0)
  }, [products?.nodesCount, updatePrintableCount])

  return (
    <>
      {loading && totalProducts === 0 && <FetchingPanel />}
      {!loading && totalProducts === 0 && <div>Add some products to get started with Fieldfolio</div>}

      {!loading && totalProducts > 0 && (
        <div className={className}>
          <ProductsFilter catalogId={catalogId} filter={filter} />
          {loading && <FetchingPanel />}
          {!loading && data?.currentSeller?.catalog && totalProducts > 0 && products && (
            <>
              <ProductsTable
                data={products}
                catalogId={data?.currentSeller?.catalog?.id}
                refreshFirstImage={refreshFirstImage}
                updatedProductId={updatedProductId}
                returnTo={returnTo}
                outOfStockThreshold={outOfStockThreshold}
                currencySymbol={currencySymbol}
              />
              <Pagination
                className={styles.pagination}
                thingName="products"
                currentPage={page}
                totalPages={products.pagesCount}
                perPage={perPage}
                totalItems={products.nodesCount}
                kind="primary"
                showSummary
              />
            </>
          )}
        </div>
      )}
    </>
  )
}

export default ProductsList
